<!-- eslint-disable vuetify/no-deprecated-props -->
<!-- ^for :ticks="radiusTickLevels" ^ -->
<template>
  <v-card>
    <v-card-title class="d-flex">
      <v-icon start color="grey">mdi-filter</v-icon> Filter
      <v-icon @click="save" class="ml-auto" end color="grey"
        >mdi-close</v-icon
      >
    </v-card-title>
    <v-card-text class="px-3 py-8">
      <v-form>
        <sorter
          v-model="sorts"
          :sortableFields="sortableFields"
          :SortTypes="SortTypes"
        />
      </v-form>
      <v-form :ref="form">
        <v-text-field
          v-model="fulltextSearch"
          variant="outlined"
          hide-details
          placeholder="Freitextsuche"
          clearable
          @click:clear="fulltextSearch = ''"
        />
        <v-select
          variant="plain"
          messages=""
          density="compact"
          :items="allSearchInItems"
          multiple
          item-title="text"
          item-value="value"
          v-model="filters['Kunde/Fulltext$Fulltext'].fields"
        >
          <template #selection="{ item }">
            <v-chip variant="outlined" class="black--color" color="grey" size="x-small">
              <span>{{ item.title }}</span>
            </v-chip>
          </template>
        </v-select>
        <v-select
          v-model="filters['Kunde/KundStatus$In']"
          :items="[
            { value: '1', text: 'Interessenten' },
            { value: '2', text: 'Kunden' },
            { value: '3', text: 'Ehemalige Kunden' },
            { value: '9', text: 'Lieferanten' },
          ]"
          multiple
          item-title="text"
          item-value="value"
          label="Kundentyp"
          clearable
        />
        <v-card-text class="pr-2 pl-0 py-0 d-flex">
          <v-slider
            v-model="filters.DistanzKm$To"
            :max="radiusMax"
            :min="radiusMin"
            :step="radiusStep"
            track-color="gray"
            show-ticks="always"
            hide-details
            density="compact"
            :ticks="radiusTickLevels"
          >
            <template #prepend>Radius</template>
            <template #append>km</template>
          </v-slider>
        </v-card-text>
      </v-form>
    </v-card-text>
    <v-divider></v-divider>

    <v-card-actions class="text-center">
      <v-btn color="grey" class="flex-1" variant="outlined" size="large" @click="reset"
        >Zurücksetzen</v-btn
      >
      <v-btn class="flex-1 btn-navigator-primary" size="large" @click="save">Speichern</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script setup lang="ts">
import {computed, ref, watch} from 'vue';
import Sorter from '../Sorter.vue';
import {FulltextSearchField, ValueType} from '@/api/meta/filters';
import {useFilterSort} from '@/composables/filterSort';
import useNearbyProvidersStore from '@/store/nearbyProviders';
import {VNodeRef} from 'vue';
const store = useNearbyProvidersStore();
const emits = defineEmits(['close']);
const {save, reset, filters, sorts, SortTypes} = useFilterSort(store, emits);

const allSearchInItems = ref<Array<{
      text: string;
      value: FulltextSearchField;
}>>([
  {
    text: 'Name',
    value: {
      path: ['Kunde/Kundname'],
      valueType: ValueType.StringContains,
    },
  },
  {
    text: 'Kunden-Nr.',
    value: {
      path: ['Kunde/KundenNr'],
      valueType: ValueType.Naked,
    },
  },
  {
    text: 'Disponent',
    value: {
      path: ['Kunde/Disponent'],
      valueType: ValueType.StringContains,
    },
  },
  {
    text: 'Beauftragter 1',
    value: {
      path: ['Kunde/VertriebsBeauftragter'],
      valueType: ValueType.StringContains,
    },
  },
  {
    text: 'Beauftragter 2',
    value: {
      path: ['Kunde/VertriebsBeauftragter2'],
      valueType: ValueType.StringContains,
    },
  },
  {
    text: 'PLZ',
    value: {
      path: ['Kunde/PostAnschrift', 'Plz'],
      valueType: ValueType.StringContains,
    },
  },
  {
    text: 'Ort',
    value: {
      path: ['Kunde/PostAnschrift', 'Ort'],
      valueType: ValueType.StringContains,
    },
  },
]);

const sortableFields = ref([
  {
    text: 'Distanz',
    value: 'DistanzKm',
  },
  {
    text: 'Kundenname',
    value: 'Kunde/Kundname',
  },
  {
    text: 'Kundenstatus',
    value: 'Kunde/KundStatus',
  },
]);
const form = ref<HTMLFormElement & VNodeRef | undefined>();

function ruleInt(val: string | number | undefined | null): string | true {
  if (!val) return true;

  if (parseInt(val as string).toString() !== val.toString()) {
    return 'bitte eine ganze Zahl eingeben';
  }

  return true;
}
function ruleUserShort(val: string | undefined | null): string | true {
  if (!val) return true;

  if (val.length > 5) return 'zu lang';

  return true;
}

const KundenNr = computed({
  get() {
    return filters.value.KundenNr;
  },
  set(KundenNr: number | undefined) {
    filters.value.KundenNr = KundenNr;
  },
});
const fulltextSearch = computed({
  set(sWord: string) {
    const words = (sWord ?? '').split(' ').filter((w) => !!w);
    filters.value['Kunde/Fulltext$Fulltext'].words = words;
  },
  get(): string {
    const words = filters.value['Kunde/Fulltext$Fulltext'].words;

    return words?.join(' ') || '';
  },
});
const radiusMin = computed((): number => {
  return 10;
});
const radiusMax = computed((): number => {
  return 200;
});
const radiusStep = computed((): number => {
  return 10;
});
const radiusTickLevels = computed((): string[] => {
  const val = filters.value.DistanzKm$To;
  const arr = [];

  for (let i = radiusMin.value; i <= radiusMax.value; i += radiusStep.value) {
    arr.push(i === val ? i.toString() : '');
  }

  return arr;
});

watch(form, () => {
  form.value?.validate();
});

defineExpose({
  Sorter,
  store,
  save, reset, filters, sorts, SortTypes,
  allSearchInItems,
  sortableFields,
  form,
  ruleInt, ruleUserShort,
  KundenNr,
  fulltextSearch,
  radiusMin,
  radiusMax,
  radiusStep,
  radiusTickLevels,
});
</script>
<style scoped>
.fixed-bottom {
  position: fixed;
  bottom: 0;
  width: 100%;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
}
.v-card__actions {
  justify-content: center;
}
</style>
